import React, { useEffect, useState, useRef, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select, { SelectInstance } from "react-select";
import { Loader2 } from "lucide-react";
import Modal from "Common/Components/Modal";
import { addCustomer, updateCustomer, getStates, getDistricts, getCities, getSalesReps, registerBusinessWithUser } from "helpers/backend_helper";
import { Common } from "helpers/common";

interface AddCustomerProps {
  onSave: (result: any) => void;
  onHide: () => void;
  data?: any;
}

const AddCustomer: React.FC<AddCustomerProps> = ({ onSave, onHide, data }) => {
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const commonHelper = new Common();
  const [isSaving, setIsSaving] = useState(false);
  const [states, setStates] = useState<any[]>([]);
  const [districts, setDistricts] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [salesReps, setSalesReps] = useState<any[]>([]);
  const [selectedState, setSelectedState] = useState<any>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<any>(null);
  const [selectedCity, setSelectedCity] = useState<any>(null);
  const [selectedSalesRep, setSelectedSalesRep] = useState<any>(null);
  const districtRef = useRef<SelectInstance<any> | null>(null);
  const cityRef = useRef<SelectInstance<any> | null>(null);

  useEffect(() => {
    loadStates();
    loadSalesReps();
    if (data && data.id) {
      setIsEdit(true);
    }
  }, [data]);

  const loadStates = useCallback(async () => {
    try {
      const response = await getStates();
      if (response.success) {
        const items = [{ value: '', label: 'Select' }, ...response.data.map((item: any) => ({ value: item.id, label: item.name }))];
        setStates(items);
        // If current selectedState is not in new items, reset it
        if (!items.find((opt: any) => opt.value === (selectedState ? selectedState.value : ''))) {
          setSelectedState(null);
          validation.setFieldValue('stateId', '');
        }
      }
    } catch (error) {
      console.error('Error loading states:', error);
      commonHelper.showErrorMessage('Failed to load states');
    }
  }, [selectedState]);

  const loadDistricts = useCallback(async (stateId: string) => {
    if (!stateId) return;
    try {
      const response = await getDistricts('value=' + stateId);
      if (response.success) {
        const items = [{ value: '', label: 'Select' }, ...response.data.map((item: any) => ({ value: item.id, label: item.name }))];
        setDistricts(items);
        if (!items.find((opt: any) => opt.value === (selectedDistrict ? selectedDistrict.value : ''))) {
          setSelectedDistrict(null);
          validation.setFieldValue('districtId', '');
        }
      }
    } catch (error) {
      console.error('Error loading districts:', error);
      commonHelper.showErrorMessage('Failed to load districts');
    }
  }, [selectedDistrict]);

  const loadCities = useCallback(async (districtId: string) => {
    if (!districtId) return;
    try {
      const response = await getCities('value=' + districtId);
      if (response.success) {
        const items = [{ value: '', label: 'Select' }, ...response.data.map((item: any) => ({ value: item.id, label: item.name }))];
        setCities(items);
        if (!items.find((opt: any) => opt.value === (selectedCity ? selectedCity.value : ''))) {
          setSelectedCity(null);
          validation.setFieldValue('cityId', '');
        }
      }
    } catch (error) {
      console.error('Error loading cities:', error);
      commonHelper.showErrorMessage('Failed to load cities');
    }
  }, [selectedCity]);

  const loadSalesReps = useCallback(async () => {
    try {
      const response = await getSalesReps();
      if (response.success) {
        const items = [{ value: '', label: 'Select' }, ...response.data.map((item: any) => ({ value: item.id, label: item.fullName }))];
        setSalesReps(items);
        // Set selected sales rep if editing
        if (data && data.salesRepId) {
          const selectedRep = items.find((opt: any) => opt.value === data.salesRepId);
          setSelectedSalesRep(selectedRep || null);
        }
      }
    } catch (error) {
      console.error('Error loading sales representatives:', error);
      commonHelper.showErrorMessage('Failed to load sales representatives');
    }
  }, [data]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      businessName: (data && data.businessName) || '',
      addressLine1: (data && data.addressLine1) || '',
      addressLine2: (data && data.addressLine2) || '',
      pinCode: (data && data.pinCode) || '',
      stateId: (data && data.stateId) || '',
      districtId: (data && data.districtId) || '',
      cityId: (data && data.cityId) || '',
      fullName: (data && data.fullName) || '',
      phone: (data && data.phone) || '',
      email: (data && data.email) || '',
      gst: (data && data.gst) || '',
      pan: (data && data.pan) || '',
      password: (data && data.password) || '',
      salesRepId: (data && data.salesRepId) || '',
    },
    validationSchema: Yup.object({
      businessName: Yup.string().required('Business name is required'),
      addressLine1: Yup.string().required('Address Line 1 is required'),
      addressLine2: Yup.string().required('Address Line 2 is required'),
      pinCode: Yup.string().required('Pin code is required').matches(/^\d{6}$/,'Pin code must be 6 digits'),
      stateId: Yup.string().required('State is required'),
      districtId: Yup.string().required('District is required'),
      cityId: Yup.string().required('City is required'),
      fullName: Yup.string().required('Full name is required'),
      phone: Yup.string().required('Phone is required').matches(/^\d{10}$/,'Phone must be 10 digits'),
      email: Yup.string().email('Invalid email').required('Email is required'),
      gst: Yup.string().optional().test('gst-format', 'Invalid GST format', function(value) {
        if (!value || value.length === 0) return true;
        return /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}\d{1}[A-Z]{1}\d{1}$/.test(value);
      }),
      pan: Yup.string().optional().test('pan-format', 'Invalid PAN format', function(value) {
        if (!value || value.length === 0) return true;
        return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value);
      }),
      password: isEdit 
        ? Yup.string().optional().min(6,'Password must be at least 6 characters')
        : Yup.string().required('Password is required').min(6,'Password must be at least 6 characters'),
      salesRepId: Yup.string().optional(),
    }),
    onSubmit: async (values) => {
      setIsSaving(true);
      try {
        const payload: any = {
          ...values,
          pinCode: Number(values.pinCode),
          stateId: Number(values.stateId),
          districtId: Number(values.districtId),
          cityId: Number(values.cityId),
          salesRepId: values.salesRepId ? Number(values.salesRepId) : null,
        };

        // Only include pan and gst if they have values
        if (!values.pan || values.pan.trim() === '') {
          delete payload.pan;
        }
        if (!values.gst || values.gst.trim() === '') {
          delete payload.gst;
        }
        
        
        if (isEdit && data && data.id) {
          payload.id = data.id;
        }
        
        const result = await (isEdit ? updateCustomer(payload) : registerBusinessWithUser(payload));
        commonHelper.showSuccessMessage(`Customer has been successfully ${isEdit ? "updated" : "added"}.`);
        onSave(result);
      } catch (error: any) {
        console.log('API-Error', error);
        commonHelper.showErrorMessage(error?.response?.data?.message || 'Failed to register customer');
      } finally {
        setIsSaving(false);
      }
    },
  });

  const handleStateChange = (item: any) => {
    // If placeholder selected, treat as null
    if (!item || item.value === '') {
      setSelectedState(null);
      setSelectedDistrict(null);
      setSelectedCity(null);
      setDistricts([]);
      setCities([]);
      validation.setFieldValue('stateId', '');
      validation.setFieldValue('districtId', '');
      validation.setFieldValue('cityId', '');
      districtRef.current?.clearValue();
      cityRef.current?.clearValue();
      return;
    }
    setSelectedState(item);
    setSelectedDistrict(null);
    setSelectedCity(null);
    setDistricts([]);
    setCities([]);
    validation.setFieldValue('stateId', item.value);
    validation.setFieldValue('districtId', '');
    validation.setFieldValue('cityId', '');
    districtRef.current?.clearValue();
    cityRef.current?.clearValue();
    loadDistricts(item.value);
  };

  const handleDistrictChange = (item: any) => {
    if (!item || item.value === '') {
      setSelectedDistrict(null);
      setSelectedCity(null);
      setCities([]);
      validation.setFieldValue('districtId', '');
      validation.setFieldValue('cityId', '');
      cityRef.current?.clearValue();
      return;
    }
    setSelectedDistrict(item);
    setSelectedCity(null);
    setCities([]);
    validation.setFieldValue('districtId', item.value);
    validation.setFieldValue('cityId', '');
    cityRef.current?.clearValue();
    loadCities(item.value);
  };

  const handleCityChange = (item: any) => {
    if (!item || item.value === '') {
      setSelectedCity(null);
      validation.setFieldValue('cityId', '');
      return;
    }
    setSelectedCity(item);
    validation.setFieldValue('cityId', item.value);
  };

  const handleSalesRepChange = (item: any) => {
    if (!item || item.value === '') {
      setSelectedSalesRep(null);
      validation.setFieldValue('salesRepId', '');
      return;
    }
    setSelectedSalesRep(item);
    validation.setFieldValue('salesRepId', item.value);
  };

  // Load initial data for edit mode
  useEffect(() => {
    if (isEdit && data) {
      // Load districts and cities if state and district are already selected
      if (data.stateId) {
        loadDistricts(data.stateId.toString());
      }
      if (data.districtId) {
        loadCities(data.districtId.toString());
      }
    }
  }, [isEdit, data, loadDistricts, loadCities]);

  return (
    <React.Fragment>
      <Modal.Header className="bg-custom-200 dark:bg-custome-700 flex items-center justify-between p-4 border-b dark:border-zink-300/20"
        closeButtonClass="transition-all duration-200 ease-linear text-slate-400 hover:text-red-500">
        <Modal.Title className="text-16">{!!isEdit ? "Edit Customer" : "Add Customer"}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="max-h-[calc(theme('height.screen')_-_80px)] p-4 overflow-y-auto bg-custom-50 dark:bg-custom-500/200">
        <form
          action="#!"
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
          autoComplete="off"
        >
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-12 ">
          <div className="xl:col-span-6">
            <label htmlFor="businessName" className="text-base font-medium">Business Name<span className='redColor'>*</span></label>
            <input type="text" id="businessName" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="Business Name"
              name="businessName"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.businessName || ""}
            />
            {validation.touched.businessName && validation.errors.businessName ? (
              <p className="text-red-400">{String(validation.errors.businessName)}</p>
            ) : null}
          </div>
                  <div className="xl:col-span-6">
            <label htmlFor="fullName" className="text-base font-medium">Full Name<span className='redColor'>*</span></label>
            <input type="text" id="fullName" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="Full Name"
              name="fullName"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.fullName || ""}
            />
            {validation.touched.fullName && validation.errors.fullName ? (
              <p className="text-red-400">{String(validation.errors.fullName)}</p>
            ) : null}
          </div>
          <div className="xl:col-span-6">
            <label htmlFor="phone" className="text-base font-medium">Phone<span className='redColor'>*</span></label>
            <input type="text" id="phone" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="Phone Number"
              name="phone"
              maxLength={10}
              onChange={e => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                validation.setFieldValue('phone', value);
              }}
              onBlur={validation.handleBlur}
              value={validation.values.phone || ""}
            />
            {validation.touched.phone && validation.errors.phone ? (
              <p className="text-red-400">{String(validation.errors.phone)}</p>
            ) : null}
          </div>
          <div className="xl:col-span-6">
            <label htmlFor="email" className="text-base font-medium">Email<span className='redColor'>*</span></label>
            <input type="email" id="email" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="Email"
              name="email"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.email || ""}
            />
            {validation.touched.email && validation.errors.email ? (
              <p className="text-red-400">{String(validation.errors.email)}</p>
            ) : null}
          </div>

          <div className="xl:col-span-6">
            <label htmlFor="password" className="text-base font-medium">Password{!isEdit && <span className='redColor'>*</span>}</label>
            <input type="password" id="password" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder={isEdit ? "Leave blank to keep current password" : "Password"}
              name="password"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.password || ""}
            />
            {validation.touched.password && validation.errors.password ? (
              <p className="text-red-400">{String(validation.errors.password)}</p>
            ) : null}
          </div>
          <div className="xl:col-span-6">
            <label htmlFor="addressLine1" className="text-base font-medium">Address Line 1<span className='redColor'>*</span></label>
            <input type="text" id="addressLine1" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="Address Line 1"
              name="addressLine1"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.addressLine1 || ""}
            />
            {validation.touched.addressLine1 && validation.errors.addressLine1 ? (
              <p className="text-red-400">{String(validation.errors.addressLine1)}</p>
            ) : null}
          </div>
          <div className="xl:col-span-6">
            <label htmlFor="addressLine2" className="text-base font-medium">Address Line 2<span className='redColor'>*</span></label>
            <input type="text" id="addressLine2" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="Address Line 2"
              name="addressLine2"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.addressLine2 || ""}
            />
            {validation.touched.addressLine2 && validation.errors.addressLine2 ? (
              <p className="text-red-400">{String(validation.errors.addressLine2)}</p>
            ) : null}
          </div>
          <div className="xl:col-span-6">
            <label htmlFor="pinCode" className="text-base font-medium">Pin Code<span className='redColor'>*</span></label>
            <input type="text" id="pinCode" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="Pin Code"
              name="pinCode"
              maxLength={6}
              onChange={e => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                validation.setFieldValue('pinCode', value);
              }}
              onBlur={validation.handleBlur}
              value={validation.values.pinCode || ""}
            />
            {validation.touched.pinCode && validation.errors.pinCode ? (
              <p className="text-red-400">{String(validation.errors.pinCode)}</p>
            ) : null}
          </div>
          <div className="xl:col-span-6">
            <label htmlFor="stateId" className="text-base font-medium">State<span className='redColor'>*</span></label>
            <Select
              options={states}
              value={states.find(opt => opt.value === validation.values.stateId) || null}
              onChange={handleStateChange}
              name="stateId"
              id="stateId"
              className="w-full"
              placeholder="Select State"
              styles={{
                menu: (provided) => ({
                  ...provided,
                  zIndex: 9999,
                  position: 'absolute' as const,
                }),
                menuPortal: (provided) => ({
                  ...provided,
                  zIndex: 9999,
                }),
              }}
              menuPortalTarget={document.body}
            />
            {validation.touched.stateId && validation.errors.stateId ? (
              <p className="text-red-400">{String(validation.errors.stateId)}</p>
            ) : null}
          </div>
          <div className="xl:col-span-6">
            <label htmlFor="districtId" className="text-base font-medium">District<span className='redColor'>*</span></label>
            <Select
              options={districts}
              value={districts.find(opt => opt.value === validation.values.districtId) || null}
              onChange={handleDistrictChange}
              name="districtId"
              id="districtId"
              className="w-full"
              ref={districtRef}
              placeholder="Select District"
              styles={{
                menu: (provided) => ({
                  ...provided,
                  zIndex: 9999,
                  position: 'absolute' as const,
                }),
                menuPortal: (provided) => ({
                  ...provided,
                  zIndex: 9999,
                }),
              }}
              menuPortalTarget={document.body}
            />
            {validation.touched.districtId && validation.errors.districtId ? (
              <p className="text-red-400">{String(validation.errors.districtId)}</p>
            ) : null}
          </div>
          <div className="xl:col-span-6">
            <label htmlFor="cityId" className="text-base font-medium">City<span className='redColor'>*</span></label>
            <Select
              options={cities}
              value={cities.find(opt => opt.value === validation.values.cityId) || null}
              onChange={handleCityChange}
              name="cityId"
              id="cityId"
              className="w-full"
              ref={cityRef}
              placeholder="Select City"
              styles={{
                menu: (provided) => ({
                  ...provided,
                  zIndex: 9999,
                  position: 'absolute' as const,
                }),
                menuPortal: (provided) => ({
                  ...provided,
                  zIndex: 9999,
                }),
              }}
              menuPortalTarget={document.body}
            />
            {validation.touched.cityId && validation.errors.cityId ? (
              <p className="text-red-400">{String(validation.errors.cityId)}</p>
            ) : null}
          </div>
          <div className="xl:col-span-6">
            <label htmlFor="salesRepId" className="text-base font-medium">Sales Representative</label>
            <Select
              options={salesReps}
              value={salesReps.find(opt => opt.value === validation.values.salesRepId) || null}
              onChange={handleSalesRepChange}
              name="salesRepId"
              id="salesRepId"
              className="w-full"
              placeholder="Select Sales Representative"
              styles={{
                menu: (provided) => ({
                  ...provided,
                  zIndex: 9999,
                  position: 'absolute' as const,
                }),
                menuPortal: (provided) => ({
                  ...provided,
                  zIndex: 9999,
                }),
              }}
              menuPortalTarget={document.body}
            />
            {validation.touched.salesRepId && validation.errors.salesRepId ? (
              <p className="text-red-400">{String(validation.errors.salesRepId)}</p>
            ) : null}
          </div>
          <div className="xl:col-span-6">
            <label htmlFor="gst" className="text-base font-medium">GST</label>
            <input type="text" id="gst" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="GST Number"
              name="gst"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.gst || ""}
            />
            {validation.touched.gst && validation.errors.gst ? (
              <p className="text-red-400">{String(validation.errors.gst)}</p>
            ) : null}
          </div>
          <div className="xl:col-span-6">
            <label htmlFor="pan" className="text-base font-medium">PAN</label>
            <input type="text" id="pan" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="PAN Number"
              name="pan"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.pan || ""}
            />
            {validation.touched.pan && validation.errors.pan ? (
              <p className="text-red-400">{String(validation.errors.pan)}</p>
            ) : null}
          </div>
      </div>
          <div className="flex justify-end gap-2 mt-4">
            <button type="reset" data-modal-close="addDocuments" className="text-red-500 transition-all duration-200 ease-linear bg-white border-white btn hover:text-red-600 focus:text-red-600 active:text-red-600 dark:bg-zink-500 dark:border-zink-500" onClick={onHide}>Cancel</button>

            {!isSaving ?
              <button type="submit" className="text-white transition-all duration-200 ease-linear btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20">
                {!!isEdit ? "Update Customer" : "Add Customer"}
              </button>
              : <button type="button" disabled className="flex items-center text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20">
                <Loader2 className="size-4 ltr:mr-2 rtl:ml-2 animate-spin" />
                Saving...
              </button>
            }
          </div>
        </form>
      </Modal.Body>
    </React.Fragment>
  );
};

export default AddCustomer; 